/* eslint-disable no-case-declarations */
import {getCookie, getQueryParams} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/common/history";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {IconButton, PopupCloseButton, TextButton} from "@FRAMEWORK/ui/desktop/button";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useGetRecently, {Recently as RecentlyType} from "@QUERIES/essential/user/useGetRecently";
import {useQueryClient} from "@tanstack/react-query";
import {Flag} from "@yb/ui/desktop/common/Flag";
import {Noimage} from "@yb/ui/desktop/common/Noimage";
import dayjs from "dayjs";
import {useEffect, useMemo} from "react";

interface Props {
	open: boolean;
	show: boolean;
	closeShow: () => void;
	closeOpen: () => void;
}

interface RecentlyData {
	[x: string]: RecentlyType[];
}

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

/** pkg_add_evCd_wish */
// const brazeWish = async (evCd: string, detailedUrl: string) => {
// 	const eventDetailPromise = await eventDetailAsync(evCd);
// 	const eventDetail = eventDetailPromise.data.body || null;

// 	const eventPricePromise = await eventPriceAsync(evCd);
// 	const eventPrice = eventPricePromise.data.body || null;
// 	const price = getPrice(eventPrice);

// 	const eventMileagePromise = await eventMileageAsync(evCd);
// 	const eventMileage = eventMileagePromise.data.body || null;
// 	// brazeUtil.pushEvent("pkg_add_evCd_wish", BrazeClickPkgAddEvCdWish(eventDetail, price, eventMileage, detailedUrl));
// };

const getWishData = (recently: RecentlyType) => {
	const {type, detailedUrl, goodsId, webCustId, goodsName, imgUrl} = recently;
	const queries = getQueryParams(decodeURIComponent(detailedUrl));

	let add = {};
	let remove = {};
	switch (type) {
		case "PKG":
		case "DMT":
			add = {
				webCustNo: webCustId,
				goodsCd: goodsId.split("-")[0],
				goodsNm: "",
				evCd: goodsId,
				diviCd: "E",
				imageThum: "",
				link: detailedUrl,
				itemCategoryCode: `${goodsId.substring(0, 1) === "K" ? "D" : "P"}`,
				cityCode: "",
				latestAmount: "",
			};
			break;
		case "HOTEL":
		case "TOUR":
			add = {
				webCustNo: webCustId,
				goodsCd: goodsId,
				goodsNm: goodsName,
				evCd: "",
				diviCd: "",
				imageThum: imgUrl,
				link: detailedUrl,
				itemCategoryCode: type === "HOTEL" ? "IC02" : "IC04",
				cityCode: queries.regionCode ?? "",
				latestAmount: "",
			};
			break;
		default:
			break;
	}
	remove = {wishList: [{...add}]};
	return {add, remove};
};

const InfoByType = ({type, information}: Pick<RecentlyType, "type" | "information">) => {
	const infoArr = information.split(";");
	return (
		<>
			{(type === "PKG" || type === "DMT") && (
				<style.BoxInfo>
					<style.AirIcon>
						<img src={`https://static.ybtour.com/images/air/${infoArr[6]}.png`} alt={infoArr[7]} />
						{infoArr[7]}
					</style.AirIcon>
					<span>{`${dayjs(infoArr[0]).format("YY.MM.DD")}${infoArr[1]}`}</span>
				</style.BoxInfo>
			)}
			{type === "RENT" && (
				<style.BoxInfo>
					<span>
						{`${dayjs(infoArr[0]).format("YY.MM.DD")}${infoArr[1]} ${infoArr[2]}`}
						<br />
						{`~${dayjs(infoArr[3]).format("YY.MM.DD")}${infoArr[4]} ${infoArr[5]}`}
					</span>
				</style.BoxInfo>
			)}
			{type === "AIR" && (
				<style.BoxInfo>
					{infoArr.length >= 7 ? (
						<>
							<span>{`${infoArr[0]}${infoArr[1]}~${infoArr[2]}${infoArr[3]}`}</span>
							<style.DefinitionList types="bar">
								<dt>{infoArr[4]}</dt>
								<dd>{`${infoArr[5]}, ${infoArr[6]}`}</dd>
							</style.DefinitionList>
						</>
					) : (
						<>
							<span>{`${infoArr[0]}${infoArr[1]}`}</span>
							<style.DefinitionList types="bar">
								<dt>{infoArr[3]}</dt>
								<dd>{`${infoArr[4]}, ${infoArr[5]}`}</dd>
							</style.DefinitionList>
						</>
					)}
				</style.BoxInfo>
			)}
			{type === "HOTEL" && (
				<style.BoxInfo>
					<span>{`${dayjs(infoArr[0]).format("YY.MM.DD")}${infoArr[1]}~${dayjs(infoArr[2]).format("YY.MM.DD")}${
						infoArr[3]
					}`}</span>
					<style.DefinitionList types="bar">
						<dt>{infoArr[4]}</dt>
						<dd>{`${infoArr[5]}, ${infoArr[6]}`}</dd>
					</style.DefinitionList>
				</style.BoxInfo>
			)}
			{type === "TOUR" && <style.BoxInfo></style.BoxInfo>}
		</>
	);
};

const FlagByType = ({type, category}: Pick<RecentlyType, "type" | "category">) => {
	const flagArr = category.split(";");
	return type === "PKG" || type === "DMT" ? (
		<Flag $type="product" $flag={flagArr[0]} text={flagArr[1]} />
	) : (
		<Flag $type="product" $flag={type.toLowerCase()} text={category} />
	);
};

/** 찜하기 */
const WishByType = (recently: RecentlyType & {wishListYn: string; isSignin: boolean}) => {
	const {type, goodsId: evCd, wishListYn, isSignin} = recently;
	// 렌트카 | 항공 비노출
	if (type === "RENT" || type === "AIR") return <></>;

	const isWish = wishListYn === "Y";
	const queryClient = useQueryClient();

	const addWish = async () => {
		const result = await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/customer/wishList`,
			method: "POST",
			data: getWishData(recently).add,
		}).catch(e => {
			throw new Error();
		});
		if (result.data.code === "0000") {
			alert("찜한 상품으로 등록되었습니다.");
			queryClient.refetchQueries({queryKey: ["useGetRecently"], type: "active"});
		} else {
			throw new Error();
		}
	};

	const removeWish = async () => {
		if (!isWish) return;
		const result = await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/customer/wishList`,
			method: "DELETE",
			data: getWishData(recently).remove,
		}).catch(e => {
			throw new Error();
		});
		if (result.data.code === "0000") {
			alert("찜한 상품이 삭제되었습니다.");
			queryClient.refetchQueries({queryKey: ["useGetRecently"], type: "active"});
		} else {
			throw new Error();
		}
	};

	const wish = async e => {
		if (!isWish) {
			/** 위시리스트 등록 */
			addWish();
			/** braze */
			// if (type === "PKG" || type === "DMT") brazeWish(evCd, detailedUrl);
		} else {
			/** 위시리스트 삭제 */
			removeWish();
		}
	};

	const onClick = async e => {
		try {
			if (!evCd) throw new Error("필수 파라미터 누락");
			if (!isSignin) {
				if (confirm("로그인이 필요한 서비스입니다.")) LoginUtil.login();
			} else {
				wish(e);
			}
		} catch (e) {
			console.error(e);
			alert(`오류가 발생하였습니다. <br/>${e}`);
		}
	};

	return (
		<IconButton icon="wishList" onClick={onClick} isActive={isWish}>
			<span>상품 찜하기</span>
		</IconButton>
	);
};

const RecentlyComponent = (props: Props) => {
	const {open, show, closeOpen, closeShow} = props;

	useEffect(() => {
		document.body.style.overflow = show ? "hidden" : "";
	}, [show]);

	const queryClient = useQueryClient();

	/** 로그인 정보 */
	const signin = useCustomerMe({throwOnError: false});
	const webCustNo = signin.data?.body.webCustNo || "";
	const isSignin = signin.data?.body.nonUser === false;

	/** 최근 본 상품 ID */
	const userId = getCookie("userId") || (window.ensureUserHasUniqueId ? window.ensureUserHasUniqueId() : "");

	/** 최근 본 리스트 */
	const recentlyListFetch = useGetRecently(userId, webCustNo, {
		enabled: !signin.isFetching && !!userId,
		throwOnError: false,
	});
	const recentlyList = recentlyListFetch.data?.body?.productResultList || [];

	// viewDate 기준 내림차순 정렬
	const recentlyData: RecentlyData = useMemo(() => {
		const sortedList = [...recentlyList].sort((a, b) => {
			if (a.viewDate < b.viewDate) return 1;
			if (a.viewDate > b.viewDate) return -1;
			return 0;
		});

		return sortedList.reduce((acc, cur) => {
			const date = dayjs(cur.viewDate).format("YYYY.MM.DD");
			acc[date] = acc[date] || [];
			acc[date].push(cur);
			return acc;
		}, {});
	}, [recentlyList]);

	/** 최근 본 삭제 */
	const deleteRecently = async (recently?: RecentlyType) => {
		/* 단일, 전체 삭제 */
		const data = recently
			? {productList: [{...recently}]}
			: {productList: [{userId, webCustId: webCustNo, goodsId: ""}]};
		await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/user/recentlyViewed`,
			method: "DELETE",
			data,
		})
			.then(res => {
				if (res.data.code === "0000") {
					/* 데이터 최신화 */
					queryClient.refetchQueries({queryKey: ["useGetRecently"], type: "active"});
				} else {
					throw new Error();
				}
			})
			.catch(e => {
				throw new Error();
			});
	};

	/** 찜목록 버튼 */
	const mypageWishButton = async () => {
		const nextUrl = `${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/wishGoods.yb`;
		if (isSignin) {
			window.location.href = nextUrl;
		} else {
			// eslint-disable-next-line no-lonely-if
			if (confirm("로그인이 필요한 서비스입니다.")) LoginUtil.login();
		}
	};

	return (
		<style.PopupContainer open={open} show={show} data-show={String(show)}>
			<style.PopupDimm onClick={closeShow} onAnimationEnd={() => !show && closeOpen()} />
			<style.PopupBlock>
				<style.PopupTop>
					<style.Title>
						최근 본 상품<span>{recentlyList.length}</span>
					</style.Title>
					<div>
						<TextButton onClick={mypageWishButton} types="base" width="basic" size="default" text="찜목록" />
						<PopupCloseButton onClose={closeShow} />
					</div>
				</style.PopupTop>
				{recentlyList.length === 0 && <style.TxtEmpty>최근 본 상품이 없습니다</style.TxtEmpty>}
				{recentlyList.length > 0 && (
					<style.PopupArea>
						<style.TopArea>
							<span>최대 30개까지 조회가 가능합니다.</span>
							<button
								onClick={() => {
									if (confirm("최근 본 상품 리스트를 모두 삭제합니다.")) deleteRecently();
								}}
							>
								전체 삭제
							</button>
						</style.TopArea>
						<style.HistoryList>
							{Object.keys(recentlyData).map(date => {
								const recentlyArr = recentlyData[date];
								return (
									<li key={date}>
										<style.TitDate>{`${dayjs(date).format("MM.DD")}(${daykor[dayjs(date).day()]})`}</style.TitDate>
										<style.ProdList>
											{recentlyArr.map((item, index) => {
												const {information, goodsName, category, type, detailedUrl, soldoutYn, wishListYn} = item;
												return (
													<style.ProdListItem soldout={soldoutYn === "Y"} key={`${item.goodsId}${index}`}>
														<style.BoxSoldout>
															<span>판매 종료</span>
															<div>
																<button onClick={() => (window.location.href = item.changedUrl)}>일정 변경</button>
																<button onClick={() => deleteRecently(item)}>삭제</button>
															</div>
														</style.BoxSoldout>
														<style.Prod>
															<style.ThumbImg types={type.toLowerCase()}>
																{item.imgUrl && <Noimage width="120px" height="80px" src={item.imgUrl} alt="" />}
															</style.ThumbImg>
															<style.ListView>
																<style.ContainerTag>
																	{/* 플래그 */}
																	<FlagByType type={type} category={category} />
																	<div>
																		{/* 위시리스트 등록 | 삭제 */}
																		<WishByType {...item} wishListYn={wishListYn} isSignin={isSignin} />
																		<style.ProdDeleteBtn onClick={() => deleteRecently(item)}>
																			<span>최근 본 상품 삭제</span>
																		</style.ProdDeleteBtn>
																	</div>
																</style.ContainerTag>
																<style.TitProd>{goodsName}</style.TitProd>
																{/* 정보 */}
																<InfoByType type={type} information={information} />
															</style.ListView>
														</style.Prod>
														<style.ProdData onClick={() => (location.href = detailedUrl)} />
													</style.ProdListItem>
												);
											})}
										</style.ProdList>
									</li>
								);
							})}
						</style.HistoryList>
					</style.PopupArea>
				)}
			</style.PopupBlock>
		</style.PopupContainer>
	);
};

const Recently = (props: Props) => (
	<ErrorBoundary>
		<RecentlyComponent {...props} />
	</ErrorBoundary>
);

export default Recently;
